import StyledField from 'common/TextField/TexField1';
import { Button, Grid } from '@mui/material';
import Filter from "common/Select/SelectMultiFilter";
import { useTranslation } from 'react-i18next';
import DynamicSelect from 'common/Select/Select';
import { themes } from 'theme/themes';
import { brandingInfo } from 'features/server/brandingSlice';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import DateRangePicker from 'common/Pickers/DateRangePicker';

interface DeliveryNoteSearchHeaderComponentProps {
  handleChangeFilter: (event: any) => void;
  handleChangeSearch: (field: string, value: any) => void;
  handleSubmitSearch: () => void;
  searchFormData: any;
  filter: string[];
  clearSearch: () => void;
  search: any;
  stationData: any;
  DeliveryNoteStatusList: any;
  handleDropdownActions: (typeName: string, action: 'searchIt' | 'loadNext', search?: string) => void;
  dropdownPage: number; 
  searchLoading: string | null; 
  dropdownSearchTerm: any; 
  setDropdownSearchTerm: any;
  setDropdownPage: any;
  stationResponse: any;
}

const DeliveryNoteSearchHeader: React.FC<DeliveryNoteSearchHeaderComponentProps> = ({
  handleChangeFilter,
  handleChangeSearch,
  handleSubmitSearch,
  searchFormData,
  filter,
  clearSearch,
  search,
  stationData,
  DeliveryNoteStatusList,
  stationResponse,
  handleDropdownActions,
  dropdownPage, searchLoading, dropdownSearchTerm, setDropdownSearchTerm, setDropdownPage
}) => {
  const stationOptions = stationData.map((item: { id: number, name: string }) => ({
    value: item.id, 
    label: item.name
  }));
  const { t } = useTranslation();
  const branding = useSelector(brandingInfo);
  const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
  const filterOptions = ['vehiclePlate', 'deliveryNoteNo', 'stationId', 'vehicleVin', 'invoiceStatus', 'clientName', 'dateInterval'];
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSubmitSearch();
    }
  };
  
  return (
    <>
      <Grid container spacing={1} className='mt-1'>
        <Grid item xs={12} sm={6} md={3} lg={2}>
          <Filter 
            label="Filter By"
            filterOptions={filterOptions}
            handleChangeFilter={handleChangeFilter}
            filter={filter}
          />
        </Grid>
        {filter.map((fieldName) => (
          <>
            {fieldName === 'dateInterval' ? <Grid item key={fieldName} xs={12} md={3}>
              <DateRangePicker
                label=''
                maxDate={dayjs()}
                placeholder={'YYYY-MM-DD → YYYY-MM-DD'}
                onChange={(value) =>
                  handleChangeSearch('dateInterval', [value?.[0] ? value[0].format('YYYY-MM-DD') : null, value?.[1] ? value[1].format('YYYY-MM-DD') : null,])
                }
                value={[
                  searchFormData?.start ? dayjs(searchFormData.start) : null,
                  searchFormData?.end ? dayjs(searchFormData.end) : null,
                ]}
              />
            </Grid> : fieldName === 'invoiceStatus' ? 
              <Grid item key={fieldName} xs={12} md={3}>
                <DynamicSelect
                  value={DeliveryNoteStatusList.find((option: any) => option.value === searchFormData?.invoiceStatus)}
                  isDisabled={false}
                  isLoading={false}
                  isClearable={false}
                  isRtl={false}
                  isSearchable={true}
                  name="invoiceStatus"
                  options={DeliveryNoteStatusList}
                  isRequired={true}
                  onChange={(e) => handleChangeSearch('invoiceStatus', e)}
                  label=""
                  onKeyDown={handleKeyPress}
                />
              </Grid> : fieldName === 'stationId' ? 
              <Grid item key={fieldName} xs={12} md={3}>
                <DynamicSelect
                  value={searchFormData?.stationId ? { value: searchFormData?.stationId, label: searchFormData?.stationName || searchFormData?.stationId } : null}
                  isDisabled={false}
                  isClearable={true}
                  isRtl={false}
                  isSearchable={true}
                  name="stationId"
                  options={stationOptions}
                  isRequired={true}
                  onChange={(e) => handleChangeSearch('stationId', e)}
                  label=""
                  onKeyDown={handleKeyPress}
                  defaultPlaceholder={t('station')}
                  isLoading={searchLoading === 'stations'}
                  onInputChange={(search: string, { action }: { action: string }) => {
                    if(action === 'input-change' || 
                      (action !== 'set-value' && action !== 'menu-close' && action !== 'input-blur')
                    ) handleDropdownActions("stations", 'searchIt', search);
                    else if(action === 'input-blur' || action === 'menu-close') {
                      if(dropdownSearchTerm || dropdownPage > 1) handleDropdownActions("stations", 'searchIt', '');
                      else setDropdownSearchTerm('');
                      setDropdownPage(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {if(!(stationResponse?.pages[0]?.data as any).last) handleDropdownActions("stations", 'loadNext')}}
                  page={dropdownPage}
                />
              </Grid> : <Grid item key={fieldName} xs={12} md={3}>
                  <StyledField
                    fullWidth={true}
                    placeholder={t(fieldName)}
                    displayPlaceholder={true}
                    handleChange={(e) => handleChangeSearch(fieldName, e.target.value)}
                    required={true}
                    value={searchFormData?.[fieldName] || ''}
                    handleKeyDown={handleKeyPress}
                  />
              </Grid>
            }
          </>
        ))}
        <Grid item xs={12} sm={6} md={2} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Button 
            variant="contained"
            disabled={filter.length ? false : true}
            onClick={handleSubmitSearch}
            sx={{ boxShadow: 'none', background: filter.length > 0 ? themeColors?.primary : '#B8C0C6' }}
            className={`text-white text-ellipsis overflow-hidden font-Saira font-[600] text-[13px] h-[35px] px-8 py-1`}
          >{t('submit')}</Button>
          {filter.length && Object.keys(search).length || filter.length > 1 ?
            <Button 
            variant="contained"
            onClick={clearSearch}
            sx={{ boxShadow: 'none', color: themeColors?.primary }}
            className={`text-ellipsis overflow-hidden font-Saira font-[600] text-[13px] bg-[#d8d1e0] h-[35px] px-8 py-1`}
          >{t('clear')}</Button>: null}
        </Grid>
      </Grid>
    </>
  );
};

export default DeliveryNoteSearchHeader;